import { Country } from 'APIs/fragments';
import { countries } from 'Constants/Constants';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';

const FILE_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB

export const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required('Title is required'),
  roles: Yup.array().min(1, 'Please select at least one role').required('Selection is required'),
  givenName: Yup.string().trim().required('First name is required'),
  familyName: Yup.string().trim().required('Last name is required'),
  dateOfBirth: Yup.string().trim().required('Date of birth is required'),
  address: Yup.object().shape({
    unitNumber: Yup.string().trim().nullable(),
    streetNumber: Yup.string().trim().required('Street number is required'),
    streetName: Yup.string().trim().required('Street name is required'),
    streetType: Yup.string().trim().required('Street type is required'),
    suburb: Yup.string().trim().nullable(),
    city: Yup.string().when('country', {
      is: (country: Country) => !['AU', 'US'].includes(country),
      then: Yup.string().trim().required('City is required').nullable(),
      otherwise: Yup.string().trim().notRequired().nullable(),
    }),
    postalCode: Yup.string().trim().required('Postal code is required'),
    state: Yup.string().when('country', {
      is: (country: Country) => ['AU', 'US'].includes(country),
      then: Yup.string().trim().required('State is required').nullable(),
      otherwise: Yup.string().trim().notRequired().nullable(),
    }),
    country: Yup.string().oneOf(countries.map((c) => c.value as Country)),
  }),
  emailAddress: Yup.string().trim().email('Must be a valid email').required('Contact email is required'),
  phoneNumber: Yup.string()
    .trim()
    .required('Phone number is required')
    .test({
      name: 'phone-no-validation',
      test: function () {
        const { phoneNumber } = this.parent;
        if (!phoneNumber)
          return this.createError({
            message: `Phone number is required`,
            path: `phoneNumber`,
          });
        if (!isPossiblePhoneNumber(phoneNumber))
          return this.createError({
            message: `Phone number is invalid`,
            path: `phoneNumber`,
          });
        return true;
      },
    }),
  passport: Yup.object()
    .when('verificationDocs.idType', {
      is: 'Passport',
      then: (schema) =>
        schema
          .shape({
            country: Yup.string().oneOf(
              countries.map((c) => c.value as Country),
              'Country is required',
            ),
            passportNumber: Yup.string().trim().required('Passport number is required'),
            expiryDate: Yup.string().trim().required('Passport expiry date is required'),
          })
          .required('Passport details required'),
    })
    .nullable(),
  driverLicence: Yup.object()
    .when('verificationDocs.idType', {
      is: 'DriverLicence',
      then: (schema) =>
        schema
          .shape({
            country: Yup.string().oneOf(
              countries.map((c) => c.value as Country),
              'Country is required',
            ),
            driverLicenceNumber: Yup.string().trim().required('Driver licence number is required'),
            documentNumber: Yup.string().when('country', {
              is: (country: Country) => country !== 'NZ',
              then: Yup.string().trim().required('Document number is required').nullable(),
              otherwise: Yup.string().trim().notRequired().nullable(),
            }),
            versionNumber: Yup.string().when('country', {
              is: (country: Country) => country === 'NZ',
              then: Yup.string().trim().required('Version number is required').nullable(),
              otherwise: Yup.string().notRequired().nullable(),
            }),
            state: Yup.string().trim().notRequired().nullable(),
          })
          .required('Driver licence details required'),
    })
    .nullable(),
  verificationDocs: Yup.object().shape({
    idType: Yup.string().oneOf(['Passport', 'DriverLicence'], 'ID type is required'),
    docFrontFileId: Yup.string().trim().required('Required'),
    docBackFileId: Yup.string().when('idType', {
      is: 'DriverLicence',
      then: Yup.string().trim().required('Required').nullable(),
      otherwise: Yup.string().notRequired().nullable(),
    }),
    docFrontFile: Yup.mixed().test('fileSize', `File too big, can't exceed 10MB limit`, (value) => {
      if (value) {
        return value.size <= FILE_SIZE_LIMIT;
      } else return true;
    }),
    docBackFile: Yup.mixed().test('fileSize', `File too big, can't exceed 10MB limit`, (value) => {
      if (value) {
        return value.size <= FILE_SIZE_LIMIT;
      } else return true;
    }),
  }),
});

export default validationSchema;
